import {Sound} from '../Sound'
import {turnSecondsInToTimerFormat} from '../ui_utils/ClientUtils'
import {UIGameManager} from './UIGameManager'
import {UIGameMechanic} from './UIGameEventController'
import {GameStateEnums} from '../../../../shared/GameEnums'
import UIGameEventEnums = GameStateEnums.UIGameEventEnums
import {MathUtils} from '../../../../shared/utils/MathUtils'
import {DurationConverter, TimeUtils} from '@colonist/utils'
import {TextView} from '../ui_canvas_engine/TextView'
import {TimeoutId} from '@colonist/types'
import {GameColor} from '../GameColor'

export abstract class UIGameTimerController implements UIGameMechanic {
    protected readonly uiGameManager: UIGameManager
    protected readonly defaultTimerColor: GameColor.Color = GameColor.Color.GrayScale0
    protected textView: TextView

    private interval: TimeoutId
    private timerEndTimestamp: number = 0
    private pausedRemainingTimeMs: number = 0
    private readonly secondsLeftBeforeTickingSound: number = 5
    protected secondsLeft: number = 0

    constructor(uiGameManager: UIGameManager) {
        this.uiGameManager = uiGameManager
        this.uiGameManager.eventController.registerUIGameMechanic(this)

        this.textView = this.createText()
        this.updateTimer()
    }

    protected abstract createText(): TextView
    protected abstract onBeginnerModeStart(): void

    onEvent(data: any, eventType: UIGameEventEnums) {
        switch(eventType) {
            case UIGameEventEnums.FinishedBuildingGame:
            case UIGameEventEnums.GameStateUpdated: this.updateTimer(); break
            case UIGameEventEnums.GameEnd: this.stop(); break
            case UIGameEventEnums.GamePauseToggled: this.setPause(data); break
            case UIGameEventEnums.CanvasResized: this.onCanvasResize(); break
            case UIGameEventEnums.ReplayModeStart:
            case UIGameEventEnums.BeginnerModeStart: this.onBeginnerModeStart(); break
            default: break
        }
    }

    private onCanvasResize() {
        this.textView.destroy()
        this.textView = this.createText()
    }

    private updateTimer() {
        if(this.uiGameManager.gameController.isReplay) return
        if(this.uiGameManager.pauseController?.isPaused()) return

        const timeAllocatedForCurrentState = TimeUtils.secondsLeftForCurrentState(this.uiGameManager.gameController.timeLeftInCurrentState)
        this.setEndTimestamp(Date.now() + DurationConverter.secondsAsMilliseconds(timeAllocatedForCurrentState))
        clearInterval(this.interval)
        this.updateTimeText()
        this.startTimer()
    }

    stop() {
        clearInterval(this.interval)
        this.setEndTimestamp(Date.now())
        this.updateTimeText()
    }

    private startTimer() {
        this.pausedRemainingTimeMs = 0

        const timerUpdateInterval = 1000
        clearInterval(this.interval)
        this.interval = setInterval(() => {
            this.updateTimeLeft()
            this.updateTimeText()
            this.playTickingReminderSoundAtEndOfTurn()
        }, timerUpdateInterval)
    }

    private updateTimeLeft() {
        this.secondsLeft = Math.max(Math.round((this.timerEndTimestamp - Date.now()) / TimeUtils.MILLISECONDS), 0)
    }

    protected updateTimeText() {
        const formattedText = turnSecondsInToTimerFormat(this.secondsLeft)
        if(this.textView.getText() != formattedText) this.textView.setText({key: 'strings:colonistCommon.utils.#buffer', options: {value: formattedText}})
        this.textView.centerX()
        this.textView.repositionY(true)

        if(this.isPlayerTakingActionAtTheEndOfTheirTurn(this.secondsLeft)) this.setTimerColorAtEndOfTurn()
        else this.setDefaultTimerColor()
    }

    private isPlayerTakingActionAtTheEndOfTheirTurn(timeLeft: number): boolean {
        const amITakingAction = this.uiGameManager.gameController.amITakingAction()
        if(!amITakingAction) return false
        if(this.uiGameManager.gameState.isGameOver) return false
        if(timeLeft > this.secondsLeftBeforeTickingSound) return false

        return true
    }

    private playTickingReminderSoundAtEndOfTurn() {
        if(!this.isPlayerTakingActionAtTheEndOfTheirTurn(this.secondsLeft)) return

        const isFirstReminder = this.secondsLeft == this.secondsLeftBeforeTickingSound
        if(isFirstReminder) Sound.playSound(Sound.SoundType.FirstReminder, 0.6, 1.0)

        const minimumVolume = 1.0
        const maximumVolume = 1.2
        const volume = MathUtils.lerp(this.secondsLeftBeforeTickingSound, 0, minimumVolume, maximumVolume, this.secondsLeft)

        const minimumPitch = 1.0
        const maximumPitch = 1.5
        const pitch = MathUtils.lerp(this.secondsLeftBeforeTickingSound, 0, minimumPitch, maximumPitch, this.secondsLeft)

        Sound.playSound(Sound.SoundType.ClockTick, volume, pitch)
    }

    protected setTimerColorAtEndOfTurn() {
        this.textView.changeStyle({fill: GameColor.getColor(GameColor.Color.RedDark)})
    }

    protected setDefaultTimerColor() {
        this.textView.changeStyle({fill: GameColor.getColor(this.defaultTimerColor)})
    }

    protected pauseTimer() {
        const remainingTime = this.timerEndTimestamp - Date.now()
        this.pausedRemainingTimeMs = remainingTime
        clearInterval(this.interval)
    }

    protected resumeTimer() {
        if(this.pausedRemainingTimeMs == 0) return
        this.setEndTimestamp(Date.now() + this.pausedRemainingTimeMs)
        this.startTimer()
    }

    private setPause(paused: boolean) {
        const alreadyPaused = this.pausedRemainingTimeMs > 0
        if(paused && alreadyPaused) return
        paused ? this.pauseTimer() : this.resumeTimer()
    }

    private setEndTimestamp(value: number) {
        this.timerEndTimestamp = value
        this.updateTimeLeft()
    }
}
